import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';

import {
  getScrollableDimensions,
  hasChartKeys,
  hasEmbeddedDataValues,
  parseSpecSafely,
  withResponsiveAutosize
} from '../services/vegaSpecUtils';

const RENDER_FAILED_MESSAGE = 'The chart could not be rendered. Try regenerating or refining it.';

export default function VegaLiteChart({ spec, data, height = '100%', overrideSpecData = false }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const viewRef = useRef(null);
  const autosizeTypeRef = useRef('fit');
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [renderFailed, setRenderFailed] = useState(false);

  const parsedSpec = parseSpecSafely(spec);
  const { hasDiscreteX, hasDiscreteY, minWidth, minHeight } = getScrollableDimensions(parsedSpec, data);
  const needsHorizontalScroll = containerSize.width > 0 && minWidth > containerSize.width;
  const needsVerticalScroll = containerSize.height > 0 && minHeight > containerSize.height;
  autosizeTypeRef.current = needsHorizontalScroll ? 'fit-y' : needsVerticalScroll ? 'fit-x' : 'fit';

  useEffect(function observeContainerSize() {
    if (!outerRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const nextEntry = entries[0];
      if (!nextEntry) {
        return;
      }

      const { width, height: nextHeight } = nextEntry.contentRect;
      setContainerSize(previousSize =>
        previousSize.width === width && previousSize.height === nextHeight
          ? previousSize
          : { width, height: nextHeight }
      );
    });

    resizeObserver.observe(outerRef.current);

    return function cleanupObserver() {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(
    function renderChart() {
      setRenderFailed(false);
      const nextParsedSpec = parseSpecSafely(spec);
      if (!innerRef.current || !nextParsedSpec) {
        return undefined;
      }

      if (!hasChartKeys(nextParsedSpec)) {
        innerRef.current.innerHTML = '';
        return undefined;
      }

      let cancelled = false;

      const chartSpec = withResponsiveAutosize(nextParsedSpec, autosizeTypeRef.current);

      const shouldUseProvidedData =
        Array.isArray(data) && (overrideSpecData || (data.length > 0 && !hasEmbeddedDataValues(chartSpec)));
      if (shouldUseProvidedData) {
        chartSpec.data = { values: data };
      }

      chartSpec.width = 'container';
      chartSpec.height = 'container';

      innerRef.current.innerHTML = '';

      import('vega-embed')
        .then(({ default: embed }) =>
          embed(innerRef.current, chartSpec, {
            actions: false,
            renderer: 'svg'
          })
        )
        .then(result => {
          if (cancelled) {
            result.view.finalize();
            return;
          }

          if (viewRef.current) {
            viewRef.current.finalize();
          }

          viewRef.current = result.view;
        })
        .catch(() => {
          if (!cancelled) {
            setRenderFailed(true);
          }
        });

      return function cleanupChart() {
        cancelled = true;
        if (viewRef.current) {
          viewRef.current.finalize();
          viewRef.current = null;
        }
      };
    },
    [spec, data, overrideSpecData]
  );

  useEffect(
    function refreshChartSize() {
      if (!viewRef.current || !containerSize.width || !containerSize.height) {
        return;
      }

      viewRef.current
        .resize()
        .runAsync()
        .catch(() => {});
    },
    [containerSize]
  );

  if (!spec) {
    return null;
  }

  return (
    <>
      {renderFailed && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {RENDER_FAILED_MESSAGE}
        </Typography>
      )}
      <div
        ref={outerRef}
        style={{
          width: '100%',
          height,
          display: renderFailed ? 'none' : undefined,
          overflowX: needsHorizontalScroll ? 'auto' : 'hidden',
          overflowY: needsVerticalScroll ? 'auto' : 'hidden'
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: '100%',
            height: '100%',
            minWidth: needsHorizontalScroll && hasDiscreteX ? `${minWidth}px` : 0,
            minHeight: needsVerticalScroll && hasDiscreteY ? `${minHeight}px` : 0
          }}
        />
      </div>
    </>
  );
}
